import { useQueryClient } from '@tanstack/react-query';
import { Copy, Download, Printer } from 'lucide-react';
import { useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router';

import { AuthLayout } from '../../components/auth/auth-layout.tsx';
import { CodeInput } from '../../components/auth/code-input.tsx';
import { QrCode } from '../../components/auth/qr-code.tsx';
import { Alert } from '../../components/ui/alert.tsx';
import { Button } from '../../components/ui/button.tsx';
import { Field } from '../../components/ui/field.tsx';
import { Loading } from '../../components/ui/skeleton.tsx';
import { api, errorMessage } from '../../lib/api.ts';
import { safeReturnPath } from '../../lib/navigation.ts';
import { refreshSession, useSession } from '../../lib/session.ts';
import { useToast } from '../../lib/toast.tsx';

type Step = 'intro' | 'scan' | 'codes';

/** Enrollment: QR (drawn locally) + manual key, confirm with a code, save recovery codes. */
export function Enroll2faPage() {
  const { data: session, isPending } = useSession();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const notify = useToast();
  const [step, setStep] = useState<Step>('intro');
  const [secret, setSecret] = useState<{ secret: string; otpauthUri: string } | null>(null);
  const [code, setCode] = useState('');
  const [codes, setCodes] = useState<string[]>([]);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const back = safeReturnPath(params.get('volver'));

  if (isPending) return <Loading />;
  if (!session) return <Navigate to="/iniciar-sesion" replace />;
  if (session.mfaEnrolled && step !== 'codes') return <Navigate to={back} replace />;

  const start = async () => {
    setBusy(true);
    setError(null);
    try {
      setSecret(
        await api<{ secret: string; otpauthUri: string }>('/auth/2fa/enroll', { method: 'POST' }),
      );
      setStep('scan');
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const confirm = async (value: string) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api<{ recoveryCodes: string[] }>('/auth/2fa/confirm', {
        method: 'POST',
        body: { code: value },
      });
      setCodes(result.recoveryCodes);
      setStep('codes');
    } catch (err) {
      setCode('');
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const finish = async () => {
    await refreshSession(queryClient);
    await navigate(back);
  };

  const codesText = `Códigos de respaldo — ${session.tenant.name}\n${session.user.email}\nCada código sirve una sola vez.\n\n${codes.join('\n')}\n`;

  if (step === 'intro') {
    return (
      <AuthLayout
        title="Activa la verificación en dos pasos"
        subtitle="Protege tu cuenta: además de la contraseña, te pediremos un código de tu teléfono."
      >
        {error && <Alert tone="danger">{error}</Alert>}
        <ol className="list-decimal space-y-2 pl-5 text-sm">
          <li>
            Instala una aplicación de autenticación en tu teléfono (por ejemplo Google
            Authenticator, Microsoft Authenticator o 2FAS).
          </li>
          <li>Escanea el código QR que te mostraremos.</li>
          <li>Escribe el código de 6 dígitos que aparece en la aplicación.</li>
        </ol>
        <Button className="w-full" loading={busy} onClick={() => void start()}>
          Mostrar código QR
        </Button>
      </AuthLayout>
    );
  }

  if (step === 'scan' && secret) {
    const grouped = secret.secret.match(/.{1,4}/g)?.join(' ') ?? secret.secret;
    return (
      <AuthLayout
        title="Escanea el código"
        subtitle="Abre tu aplicación de autenticación y escanea este código."
      >
        {error && <Alert tone="danger">{error}</Alert>}
        <div className="flex justify-center">
          <QrCode value={secret.otpauthUri} label="Código QR para tu aplicación de autenticación" />
        </div>
        <div className="space-y-1 text-sm">
          <p className="text-muted-foreground">
            ¿No puedes escanearlo? Escribe esta clave en la aplicación:
          </p>
          <div className="flex items-center gap-2">
            <code
              data-testid="totp-secret"
              className="flex-1 rounded-lg bg-muted p-2 font-mono break-all"
            >
              {grouped}
            </code>
            <Button
              variant="secondary"
              size="icon"
              aria-label="Copiar clave"
              onClick={() => {
                void navigator.clipboard.writeText(secret.secret).then(() => {
                  notify('Clave copiada', 'success');
                });
              }}
            >
              <Copy />
            </Button>
          </div>
        </div>
        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            void confirm(code);
          }}
        >
          <Field label="Código de 6 dígitos">
            {(a11y) => (
              <CodeInput
                {...a11y}
                value={code}
                onChange={setCode}
                onComplete={(value) => {
                  void confirm(value);
                }}
              />
            )}
          </Field>
          <Button
            variant="brand"
            type="submit"
            className="w-full"
            loading={busy}
            disabled={code.length !== 6}
          >
            Activar
          </Button>
        </form>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Guarda tus códigos de respaldo"
      subtitle="Si pierdes tu teléfono, podrás entrar con uno de estos códigos. Cada uno sirve una sola vez y no volveremos a mostrarlos."
    >
      <ul
        aria-label="Códigos de respaldo"
        className="grid grid-cols-2 gap-2 rounded-lg bg-muted p-3 font-mono text-sm"
      >
        {codes.map((c) => (
          <li key={c}>{c}</li>
        ))}
      </ul>
      <div className="grid grid-cols-3 gap-2">
        <Button
          variant="secondary"
          onClick={() => {
            const url = URL.createObjectURL(
              new Blob([codesText], { type: 'text/plain;charset=utf-8' }),
            );
            const link = document.createElement('a');
            link.href = url;
            link.download = 'codigos-de-respaldo.txt';
            link.click();
            URL.revokeObjectURL(url);
          }}
        >
          <Download /> Descargar
        </Button>
        <Button
          variant="secondary"
          onClick={() => {
            void navigator.clipboard.writeText(codesText).then(() => {
              notify('Códigos copiados', 'success');
            });
          }}
        >
          <Copy /> Copiar
        </Button>
        <Button
          variant="secondary"
          onClick={() => {
            window.print();
          }}
        >
          <Printer /> Imprimir
        </Button>
      </div>
      <label className="flex items-start gap-3 text-sm">
        <input
          type="checkbox"
          className="mt-0.5 size-5"
          checked={saved}
          onChange={(event) => {
            setSaved(event.target.checked);
          }}
        />
        Guardé mis códigos de respaldo en un lugar seguro.
      </label>
      <Button className="w-full" disabled={!saved} onClick={() => void finish()}>
        Continuar
      </Button>
    </AuthLayout>
  );
}
